<template>
  <div class="inspection-route-map-container">
    <!-- 搜索和定位控制栏 -->
    <div class="map-toolbar">
      <div class="search-box">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索地点"
          class="search-input"
          clearable
          @clear="clearSearch"
          @keyup.enter.native="searchLocation"
        >
          <i slot="prefix" class="el-input__icon el-icon-search"></i>
        </el-input>
        <el-button type="primary" icon="el-icon-search" @click="searchLocation">搜索</el-button>
      </div>
      <el-button type="primary" plain icon="el-icon-location" @click="locateCurrentPosition" class="locate-btn">
        定位到当前位置
      </el-button>
    </div>

    <!-- 搜索结果下拉列表 -->
    <div v-if="searchResults.length > 0" class="search-results">
      <div
        v-for="(result, index) in searchResults"
        :key="index"
        class="search-result-item"
        @click="goToSearchResult(result)"
      >
        <div class="result-title">{{ result.title }}</div>
        <div class="result-address">{{ result.address }}</div>
      </div>
    </div>

    <!-- 地图容器 -->
    <div id="inspectionRouteMap" style="width: 100%; height: 600px;"></div>
  </div>
</template>

<script>
import { listInspectionRoutePoints } from '@/api/inspection/inspectionPointApi'
import { getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'InspectionRouteMap',
  data() {
    return {
      map: null,
      communityId: '',
      routePoints: [],
      searchKeyword: '',
      searchResults: [],
      geocoder: null,
      mapKey: '',
      currentLocationMarker: null,
      mapReady: false
    }
  },
  async mounted() {
    this.communityId = getCommunityId()
    // 等待地图SDK加载完成
    await this.waitForMapSDK()
  },
  methods: {
    // 等待腾讯地图SDK加载
    waitForMapSDK() {
      return new Promise((resolve) => {
        // 如果TMap已经加载完成且有key，直接resolve
        if (window.TMap && window.TMap.key) {
          this.mapKey = window.TMap.key
          this.mapReady = true
          console.log('[地图SDK] 已加载，密钥:', this.mapKey.substring(0, 4) + '****')
          resolve()
          return
        }

        // TMap存在但key为空，手动设置key
        if (window.TMap) {
          window.TMap.key = 'VCPBZ-T4JYU-U6DVD-GN6XF-2WBLH-BQFT7'
          this.mapKey = window.TMap.key
          this.mapReady = true
          console.log('[地图SDK] 手动设置密钥:', this.mapKey.substring(0, 4) + '****')
          resolve()
          return
        }

        // 动态加载SDK
        console.log('[地图SDK] 动态加载腾讯地图SDK')
        const script = document.createElement('script')
        script.src = 'https://map.qq.com/api/gljs?v=1.exp&key=VCPBZ-T4JYU-U6DVD-GN6XF-2WBLH-BQFT7'
        script.onload = () => {
          // 加载完成后手动设置key
          if (window.TMap) {
            window.TMap.key = 'VCPBZ-T4JYU-U6DVD-GN6XF-2WBLH-BQFT7'
            this.mapKey = window.TMap.key
            this.mapReady = true
            console.log('[地图SDK] 加载成功，密钥:', this.mapKey.substring(0, 4) + '****')
          }
          resolve()
        }
        script.onerror = (e) => {
          console.error('[地图SDK] SDK加载失败', e)
          resolve()
        }
        document.head.appendChild(script)
      })
    },

    loadData(route) {
      this.initMap()

      this.loadRoute(route)
    },

    initMap() {
      // 若已有地图或图层，先清理避免重复初始化
      if (this.markerLayer) {
        this.markerLayer.setMap(null)
        this.markerLayer = null
      }
      if (this.labelLayer) {
        this.labelLayer.setMap(null)
        this.labelLayer = null
      }
      if (this.polylineLayer) {
        this.polylineLayer.setMap(null)
        this.polylineLayer = null
      }
      if (this.currentLocationMarker) {
        this.currentLocationMarker.setMap(null)
        this.currentLocationMarker = null
      }
      if (this.map) {
        this.map.destroy()
        this.map = null
      }

      // 默认中心点（北京）
      const center = new window.TMap.LatLng(39.916527, 116.397128)

      // 创建地图实例
      this.map = new window.TMap.Map(document.getElementById('inspectionRouteMap'), {
        center: center,
        zoom: 13,
        baseMap: {
          type: 'vector',
          features: ['base', 'building2d', 'point']
        }
      })

      // 初始化服务
      this.initServices()
    },

    initServices() {
      console.log('[地图服务初始化] 开始初始化地图服务')
      if (window.TMap && window.TMap.service) {
        console.log('[地图服务初始化] TMap.service 可用，初始化地理编码服务')
        this.geocoder = new window.TMap.service.Geocoder()
      } else {
        console.warn('[地图服务初始化] TMap.service 不可用，逆地址解析功能将无法使用')
      }
      // 获取腾讯地图API密钥
      this.mapKey = window.TMap && window.TMap.key ? window.TMap.key : ''
      if (this.mapKey) {
        console.log('[地图服务初始化] 成功获取地图密钥，密钥前4位:', this.mapKey.substring(0, 4) + '****')
      } else {
        console.error('[地图服务初始化] 地图密钥未配置，搜索功能将无法使用')
      }
    },

    // 搜索位置 - 使用腾讯地图WebService API
    searchLocation() {
      console.log('[位置搜索] 开始搜索，关键词:', this.searchKeyword)

      if (!this.searchKeyword || !this.searchKeyword.trim()) {
        console.warn('[位置搜索] 搜索关键词为空')
        this.$message.warning('请输入搜索关键词')
        return
      }

      // 确保SDK已加载
      if (!this.mapKey) {
        // 再次尝试获取key
        if (window.TMap && window.TMap.key) {
          this.mapKey = window.TMap.key
          this.mapReady = true
        } else {
          console.error('[位置搜索] 地图密钥未配置，无法执行搜索')
          this.$message.warning('地图加载中，请稍后再试')
          return
        }
      }

      var _this = this
      var keyword = encodeURIComponent(this.searchKeyword.trim())
      console.log('[位置搜索] 编码后的关键词:', keyword)

      // 使用JSONP方式调用腾讯地图WebService搜索接口
      var url = 'https://apis.map.qq.com/ws/place/v1/search?keyword=' + keyword + '&boundary=region(全国,0)&key=' + this.mapKey + '&output=jsonp&callback=JSONP_CALLBACK'
      console.log('[位置搜索] 请求URL:', url.replace(this.mapKey, this.mapKey.substring(0, 4) + '****'))

      // 创建JSONP请求
      var script = document.createElement('script')
      script.src = url
      console.log('[位置搜索] 创建JSONP请求')

      // 设置JSONP回调函数
      window.JSONP_CALLBACK = function(response) {
        console.log('[位置搜索] 收到响应，状态码:', response.status, '数据条数:', response.data ? response.data.length : 0)
        delete window.JSONP_CALLBACK
        if (script.parentNode) {
          document.body.removeChild(script)
        }

        if (response.status === 0 && response.data && response.data.length > 0) {
          console.log('[位置搜索] 搜索成功，结果:', JSON.stringify(response.data.slice(0, 3)))
          _this.searchResults = response.data.slice(0, 10)
          _this.$message.success('找到 ' + _this.searchResults.length + ' 个结果')
        } else {
          console.warn('[位置搜索] 搜索失败，状态:', response.status, '信息:', response.message)
          _this.searchResults = []
          _this.$message.info('未找到相关位置')
        }
      }

      // 超时处理
      setTimeout(function() {
        if (window.JSONP_CALLBACK) {
          console.warn('[位置搜索] 请求超时')
          delete window.JSONP_CALLBACK
          if (script.parentNode) {
            document.body.removeChild(script)
          }
          _this.$message.error('搜索超时，请重试')
        }
      }, 10000)

      script.onerror = function(e) {
        console.error('[位置搜索] 网络请求失败', e)
        delete window.JSONP_CALLBACK
        if (script.parentNode) {
          document.body.removeChild(script)
        }
        _this.$message.error('搜索失败，请检查网络连接')
      }

      document.body.appendChild(script)
      console.log('[位置搜索] JSONP脚本已添加到DOM，等待响应...')
    },

    async loadRoute(route) {
      if (!route || !route.inspectionRouteId) return

      try {
        const params = {
          communityId: this.communityId,
          inspectionRouteId: route.inspectionRouteId,
          page: 1,
          row: 1000
        }

        const response = await listInspectionRoutePoints(params)
        this.routePoints = response.inspectionPoints
        this.addPointsToMap(this.routePoints)
      } catch (error) {
        console.error('获取路线点失败:', error)
        this.$message.error(this.$t('inspectionRouteMap.fetchError'))
      }
    },

    // 定位到当前位置
    locateCurrentPosition() {
      console.log('[GPS定位] 开始获取当前位置')

      if (!this.map) {
        console.warn('[GPS定位] 地图未初始化')
        this.$message.warning('地图尚未初始化')
        return
      }

      // 使用浏览器原生定位
      if (navigator.geolocation) {
        console.log('[GPS定位] 浏览器支持地理定位，开始请求位置')
        navigator.geolocation.getCurrentPosition(
          (position) => {
            var lat = position.coords.latitude
            var lng = position.coords.longitude
            var accuracy = position.coords.accuracy
            console.log('[GPS定位] 获取位置成功，纬度:', lat, '经度:', lng, '精度:', accuracy, '米')
            this.showCurrentLocation(lat, lng)
          },
          (error) => {
            console.error('[GPS定位] 获取位置失败，错误码:', error.code, '错误信息:', error.message)
            var errorMsg = '定位失败，请检查浏览器定位权限设置'
            if (error.code === 1) {
              errorMsg = '定位失败，请允许浏览器获取位置权限'
            } else if (error.code === 2) {
              errorMsg = '定位失败，无法获取位置信息'
            } else if (error.code === 3) {
              errorMsg = '定位超时，请重试'
            }
            this.$message.error(errorMsg)
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          }
        )
      } else {
        console.error('[GPS定位] 当前浏览器不支持地理定位')
        this.$message.error('当前浏览器不支持地理定位')
      }
    },

    // 在地图上显示当前位置
    showCurrentLocation(lat, lng) {
      console.log('[显示位置] 开始在地图上显示位置，纬度:', lat, '经度:', lng)

      var position = new window.TMap.LatLng(lat, lng)

      // 清除之前的定位标记
      if (this.currentLocationMarker) {
        console.log('[显示位置] 清除之前的定位标记')
        this.currentLocationMarker.setMap(null)
      }

      // 创建当前位置标记
      console.log('[显示位置] 创建定位标记')
      this.currentLocationMarker = new window.TMap.MultiMarker({
        map: this.map,
        styles: {
          'currentLocation': new window.TMap.MarkerStyle({
            'width': 30,
            'height': 30,
            'src': 'https://mapapi.qq.com/web/jsapi/demo/img/center.gif',
            'anchor': { x: 15, y: 15 }
          })
        },
        geometries: [{
          id: 'current_location',
          styleId: 'currentLocation',
          position: position
        }]
      })

      // 地图中心点移到当前位置
      console.log('[显示位置] 移动地图中心点，设置缩放级别为16')
      this.map.setCenter(position)
      this.map.setZoom(16)

      // 可选：逆地址解析显示当前位置地址
      if (this.geocoder) {
        console.log('[显示位置] 准备进行逆地址解析')
        this.geocoder.reverseGeocode({ location: position })
          .then((result) => {
            var address = result.result && result.result.formatted_addresses ? result.result.formatted_addresses.recommend : (result.result ? result.result.address : '')
            console.log('[显示位置] 逆地址解析成功，地址:', address)
            this.$message.success('已定位到当前位置')
          })
          .catch((error) => {
            console.error('[显示位置] 逆地址解析失败:', error)
          })
      } else {
        console.log('[显示位置] 地理编码服务不可用，跳过地址解析')
        this.$message.success('已定位到当前位置')
      }
    },

    // 点击搜索结果，定位到该位置
    goToSearchResult(result) {
      console.log('[搜索结果] 点击搜索结果:', JSON.stringify(result))

      if (!this.map) {
        console.warn('[搜索结果] 地图未初始化')
        return
      }

      // 腾讯地图WebService返回的location格式
      var lat = result.location && result.location.lat
      var lng = result.location && result.location.lng
      console.log('[搜索结果] 解析坐标，纬度:', lat, '经度:', lng)

      if (lat && lng) {
        var position = new window.TMap.LatLng(lat, lng)

        // 清除之前的定位标记
        if (this.currentLocationMarker) {
          console.log('[搜索结果] 清除之前的定位标记')
          this.currentLocationMarker.setMap(null)
        }

        // 创建搜索结果标记
        console.log('[搜索结果] 创建搜索结果标记')
        this.currentLocationMarker = new window.TMap.MultiMarker({
          map: this.map,
          styles: {
            'searchResult': new window.TMap.MarkerStyle({
              'width': 30,
              'height': 40,
              'src': 'https://mapapi.qq.com/web/jsapi/demo/img/center.gif',
              'anchor': { x: 15, y: 35 }
            })
          },
          geometries: [{
            id: 'search_result',
            styleId: 'searchResult',
            position: position
          }]
        })

        // 地图中心点移到搜索位置
        console.log('[搜索结果] 移动地图中心点，设置缩放级别为16')
        this.map.setCenter(position)
        this.map.setZoom(16)
        console.log('[搜索结果] 定位完成')
      } else {
        console.warn('[搜索结果] 无法获取有效坐标')
      }

      // 清除搜索结果列表
      console.log('[搜索结果] 清除搜索结果列表')
      this.searchResults = []
    },

    // 清除搜索
    clearSearch() {
      this.searchResults = []
      this.searchKeyword = ''
    },

    addPointsToMap(points) {
      if (!this.map || !points || points.length === 0) return

      // 清除之前的标记
      if (this.markerLayer) this.markerLayer.setMap(null)
      if (this.labelLayer) this.labelLayer.setMap(null)
      if (this.polylineLayer) this.polylineLayer.setMap(null)

      const geometriesMarker = []
      const geometriesLabel = []
      const path = []

      points.forEach(point => {
        if (!point.lat || !point.lng) return

        const position = new window.TMap.LatLng(point.lat, point.lng)

        // 添加标记
        geometriesMarker.push({
          id: point.inspectionId,
          styleId: 'myStyle',
          position: position
        })

        // 添加标签
        geometriesLabel.push({
          id: `label_${point.inspectionId}`,
          styleId: 'label',
          position: position,
          content: point.inspectionName
        })

        // 添加路径点
        path.push(position)
      })

      // 创建标记图层
      this.markerLayer = new window.TMap.MultiMarker({
        map: this.map,
        styles: {
          "myStyle": new window.TMap.MarkerStyle({
            width: 25,
            height: 35,
            src: '/img/maper.png',
            anchor: { x: 32, y: 32 }
          })
        },
        geometries: geometriesMarker
      })

      // 创建标签图层
      this.labelLayer = new window.TMap.MultiLabel({
        map: this.map,
        styles: {
          'label': new window.TMap.LabelStyle({
            color: '#3777FF',
            size: 20,
            offset: { x: 0, y: 15 },
            angle: 0,
            alignment: 'center',
            verticalAlignment: 'middle'
          })
        },
        geometries: geometriesLabel
      })

      // 创建路径图层
      this.polylineLayer = new window.TMap.MultiPolyline({
        map: this.map,
        styles: {
          'style_blue': new window.TMap.PolylineStyle({
            color: '#3777FF',
            width: 6,
            borderWidth: 5,
            borderColor: '#FFF',
            lineCap: 'butt'
          })
        },
        geometries: [{
          id: 'pl_1',
          styleId: 'style_blue',
          paths: path
        }]
      })

      // 如果有路径点，调整地图中心点和缩放级别
      if (path.length > 0) {
        this.map.setCenter(path[0])
        this.map.setZoom(16)
      }
    }
  },
  beforeDestroy() {
    // 组件销毁时清除地图实例
    if (this.map) {
      this.map.destroy()
      this.map = null
    }
  }
}
</script>

<style scoped>
.inspection-route-map-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.map-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: relative;
  z-index: 100;
}

.search-box {
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 400px;
}

.search-input {
  width: 280px;
  margin-right: 8px;
}

.locate-btn {
  white-space: nowrap;
}

.search-results {
  position: absolute;
  top: 70px;
  left: 12px;
  width: 380px;
  max-height: 400px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.search-result-item {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover {
  background-color: #f5f5f5;
}

.result-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.result-address {
  font-size: 12px;
  color: #999;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>